import React, { useMemo, useState } from "react";
import {
  Stack,
  Group,
  Text,
  ActionIcon,
  TextInput,
  Switch,
  Box,
} from "@mantine/core";
import { IconChevronLeft } from "@tabler/icons-react";
import { useSalon } from "../../../context/SalonContext";
import { BusinessInfo } from "../../../types/context";
import { generateBookingLink } from "../../../utils/bookingLinkUtils";

interface BookingLinkContent {
  copy?: string;
  useCustomLink?: boolean;
  link?: string;
}

interface WebsiteGeneratorBookingLinkContentProps {
  bookingLinkContent?: BookingLinkContent;
  onContentChange: (content: BookingLinkContent) => void;
  onBack: () => void;
}

const WebsiteGeneratorBookingLinkContent: React.FC<
  WebsiteGeneratorBookingLinkContentProps
> = ({ bookingLinkContent, onContentChange, onBack }) => {
  const { businessInfo } = useSalon() as { businessInfo?: BusinessInfo | null };
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const defaultBookingLink = useMemo(
    () => generateBookingLink(businessInfo?.id),
    [businessInfo?.id],
  );

  const handleCopyChange = (value: string) => {
    if (value.length > 100) {
      setValidationErrors((prev) => ({
        ...prev,
        copy: `copy cannot exceed 100 characters`,
      }));
      return;
    }
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.copy;
      return next;
    });
    onContentChange({ ...(bookingLinkContent || {}), copy: value });
  };

  const handleToggleCustom = (checked: boolean) => {
    const next: BookingLinkContent = {
      ...(bookingLinkContent || {}),
      useCustomLink: checked,
    };
    if (!checked) {
      next.link = defaultBookingLink;
    } else {
      next.copy = bookingLinkContent?.copy ?? "Book Now";
      next.link = bookingLinkContent?.link ?? defaultBookingLink;
    }
    onContentChange(next);
  };

  const handleLinkChange = (value: string) => {
    if (value.length > 300) {
      setValidationErrors((prev) => ({
        ...prev,
        link: `link cannot exceed 300 characters`,
      }));
      return;
    }
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.link;
      return next;
    });
    onContentChange({ ...(bookingLinkContent || {}), link: value });
  };

  const isCustom = !!bookingLinkContent?.useCustomLink;
  const effectiveLink = isCustom
    ? bookingLinkContent?.link || ""
    : defaultBookingLink;

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between" align="center">
        <ActionIcon variant="subtle" onClick={onBack}>
          <IconChevronLeft size={16} />
        </ActionIcon>
        <Text fw={600} size="lg">
          Booking Link
        </Text>
      </Group>

      {/* Custom booking link toggle */}
      <Box>
        <Group justify="space-between" align="flex-start">
          <Box style={{ flex: 1 }}>
            <Text size="sm" fw={500} mb="xs">
              Custom booking link
            </Text>
            <Text size="xs" c="dimmed">
              Enable to provide a custom booking URL
            </Text>
          </Box>
          <Switch
            checked={isCustom}
            onChange={(event) =>
              handleToggleCustom(event.currentTarget.checked)
            }
            style={{ marginTop: "4px" }}
          />
        </Group>
      </Box>

      {isCustom && (
        <>
          {/* Booking Link Copy */}
          <Box>
            <Text size="sm" fw={500} mb="xs">
              Booking Link Copy
            </Text>
            <TextInput
              placeholder="Book Now"
              value={bookingLinkContent?.copy ?? "Book Now"}
              onChange={(event) => handleCopyChange(event.currentTarget.value)}
              error={validationErrors.copy}
              maxLength={100}
            />
          </Box>

          {/* Booking Link */}
          <Box>
            <Text size="sm" fw={500} mb="xs">
              Booking Link
            </Text>
            <TextInput
              placeholder={defaultBookingLink}
              value={effectiveLink}
              onChange={(event) => handleLinkChange(event.currentTarget.value)}
              error={validationErrors.link}
              maxLength={300}
            />
          </Box>
        </>
      )}
    </Stack>
  );
};

export default WebsiteGeneratorBookingLinkContent;
